import { Typography } from "@mui/material";
import localize from "../../common/localize";
import {
  getBarChartOptions,
  getDonutChartOptions,
  getLineChartoptions,
} from "./chartOptionsUtils";
import BarChart from "./charts/BarChart";
import DonutChart from "./charts/DonutChart";
import OperationalStatusStatistics from "./OperationalStatusStatistic";
import SeriesChart from "./charts/SeriesChart";
import { OperationalStatusEnum } from "../../common/utils";
import { useEffect, useState } from "react";
import DonutChartComponent from "./DonutChartComponent";
import BarChartComponent from "./BarChartComponent";

const getOperatinoalStatusData = (data, id) => {
  if (data && id >= -1) {
    if (id === -1) {
      let count = 0;
      data.map((x) => (count += x.dataCount));

      return count;
    }

    const index = data.findIndex((x) => x.operationalStatusId === id);
    return index >= 0 ? data[index].dataCount : 0;
  }

  return 0;
};

const EventStatusChart = ({ data, height, width }) => {
  const eventsByStatusOptionsData = getDonutChartOptions({
    data,
    type: "status",
  });

  return (
    <DonutChart
      options={eventsByStatusOptionsData.options}
      series={eventsByStatusOptionsData.series}
      height={height}
      width={width}
    />
  );
};

const EventPersonalChart = ({ data, height, width }) => {
  const eventsPersonalOptionsData = getBarChartOptions({
    data,
    type: "personal",
    horizontalBar: true,
  });

  return (
    <BarChart
      options={eventsPersonalOptionsData?.options}
      series={eventsPersonalOptionsData?.series}
      height={height}
      width={width}
    />
  );
};

const EventTypeChart = ({ data, height, width }) => {
  const eventsTypeOptionsData = getBarChartOptions({
    data,
    type: "type",
    horizontalBar: true,
  });

  return (
    <BarChart
      options={eventsTypeOptionsData?.options}
      series={eventsTypeOptionsData?.series}
      height={height}
      width={width}
    />
  );
};

const EventByProceedingChart = ({ data, height, width }) => {
  const eventsProceedingOptionsData = getBarChartOptions({
    data,
    type: "proceeding",
    horizontalBar: true,
  });

  return (
    <BarChart
      options={eventsProceedingOptionsData?.options}
      series={eventsProceedingOptionsData?.series}
      height={height}
      width={width}
    />
  );
};

const EventsByStateChart = ({ data, height, width }) => {
  const eventsByStateData = getDonutChartOptions({
    data,
    type: "state",
  });

  return (
    <DonutChart
      options={eventsByStateData?.options}
      series={eventsByStateData?.series}
      height={height}
      width={width}
    />
  );
};

const EventsByCityChart = ({ data, height, width }) => {
  const eventsByCityData = getDonutChartOptions({
    data,
    type: "city",
  });

  return (
    <DonutChart
      options={eventsByCityData?.options}
      series={eventsByCityData?.series}
      height={height}
      width={width}
    />
  );
};

const EventsPaymentChart = ({
  data,
  height,
  width,
  initialDate,
  finalDate,
  isMobile,
}) => {
  const eventsPaymentOptionsData = getLineChartoptions({
    data,
    height,
    width,
    initialDate,
    finalDate,
    isMobile,
  });

  return (
    <SeriesChart
      options={eventsPaymentOptionsData.options}
      series={eventsPaymentOptionsData.series}
    />
  );
};

export default function GeneralStatistics({
  eventsByOperationalStatus,
  eventsByStatus,
  eventsDistributionOnPersonal,
  eventsPayment,
  eventsByState,
  eventsByCity,
  eventsByType,
  eventsByProceeding,
  initialDate,
  finalDate,
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 767);
  const handleResize = () => {
    setIsMobile(window.innerWidth < 767);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <section className="flex-col justify-center">
      <div className="flex-col w-full mt-5">
        <div
          className="flex-col w-full mb-4"
          style={{
            gap: "10px",
            justifyContent: "center",
            padding: "20px",
            boxShadow:
              "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
            borderRadius: "10px",
          }}
        >
          <div className="mb-2">
            <Typography
              sx={{
                fontSize: "0.8em",
                fontWeight: 400,
                textAlign: "center",
              }}
            >
              <b>{localize.operational_status_analytics}</b>
            </Typography>
          </div>

          <div className="flex gap-2 w-full items-center mb-2 justify-evenly flex-wrap">
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.ToExecute}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.ToExecute
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.InExecution}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.InExecution
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.PendingReview}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.PendingReview
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.Finished}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.Finished
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.NotFinished}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.NotFinished
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.Canceled}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.Canceled
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />

            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.TotalServiceOrders}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.TotalServiceOrders
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
          </div>
        </div>

        <div className="flex gap-2 w-full">
          {initialDate && finalDate && (
            <div
              className="w-full"
              style={{
                padding: "20px",
                boxShadow:
                  "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
                borderRadius: "10px",
              }}
            >
              <EventsPaymentChart
                data={eventsPayment}
                initialDate={initialDate}
                finalDate={finalDate}
                isMobile={isMobile}
              />
            </div>
          )}
        </div>
      </div>
      <div className="mt-5 w-full flex gap-2 flex-wrap justify-between">
        {eventsByStatus && (
          <DonutChartComponent
            title={localize.execution_status_analytics}
            chartComponent={
              <EventStatusChart
                width="100%"
                height={"280px"}
                data={eventsByStatus}
              />
            }
          />
        )}

        {eventsByState && (
          <DonutChartComponent
            title={localize.events_by_state_analytics}
            chartComponent={
              <EventsByStateChart
                width="100%"
                height={"280px"}
                data={eventsByState}
              />
            }
          />
        )}

        {eventsByCity && (
          <DonutChartComponent
            title={localize.events_by_city_analytics}
            chartComponent={
              <EventsByCityChart
                width="100%"
                height={"280px"}
                data={eventsByCity}
              />
            }
          />
        )}
      </div>

      <div className="mt-5 w-full flex-wrap flex gap-2 flex-wrap justify-between">
        {eventsByType && (
          <BarChartComponent
            title={localize.events_by_type}
            chartComponent={<EventTypeChart width="100%" data={eventsByType} />}
          />
        )}

        {eventsByProceeding && (
          <BarChartComponent
            title={localize.events_by_proceeding}
            chartComponent={
              <EventByProceedingChart width="100%" data={eventsByProceeding} />
            }
          />
        )}

        {eventsDistributionOnPersonal && (
          <BarChartComponent
            title={localize.events_by_distribution_on_personal}
            chartComponent={
              <EventPersonalChart
                width="100%"
                data={eventsDistributionOnPersonal}
              />
            }
          />
        )}
      </div>
    </section>
  );
}
